import { Component, OnInit, Input } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { DataTableComponent, GroupedHeaderCell, TableColumn } from 'src/app/components/commondataplatform/shared-components/data-table/data-table.component';
import { FiscalYearNavigatorComponent } from 'src/app/components/commondataplatform/shared-components/fiscal-year-navigator/fiscal-year-navigator.component';
import { SearchableSelectComponent } from 'src/app/components/commondataplatform/shared-components/searchable-select/searchable-select.component';
import { Project } from 'src/app/interfaces/investmentcontrolling/inco-project';
import { CustomDialogService } from 'src/app/services/customdialog.service';
import { DefaultValuesService } from 'src/app/services/defaultvalues.service';
import { InvestmentControllingService } from 'src/app/services/investment-controlling.service';
import { FiscalYearUtils } from 'src/app/utils/fiscal-year.utils';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { TableUtils } from 'src/app/utils/table-utils';

const FY_WINDOW = 3;
const OPL_YEARS_BACK = 2;
const OPL_YEARS_FORWARD = 1;
const OPL_FORECAST_LABELS = ['FC', 'BUD', 'PL1', 'PL2'];

type MaintExpMode = 'ACT' | 'OPL';
type ProjectVersion = 'ACTUAL' | 'BUDGET';

interface MaintExpRow {
  id: number;
  projectTitle: string;
  [key: string]: number | string | null | undefined;
}

interface MaintExpComboItem {
  id: number;
  projectTitle: string;
  displayLabel: string;
}

@Component({
  selector: 'app-maintenance-expansion-config',
  standalone: true,
  imports: [
    SearchableSelectComponent,
    FiscalYearNavigatorComponent,
    DataTableComponent,
    MatIconModule,
    MatButtonModule,
    FormsModule
  ],
  templateUrl: './maintenance-expansion-config.component.html',
  styleUrls: ['./maintenance-expansion-config.component.less']
})
export class MaintenanceExpansionConfigComponent implements OnInit {
  @Input() mode: MaintExpMode = 'ACT';
  // How many decimal places M/E values accept (0..2 — the DB column is decimal(5,2)).
  @Input() decimalPlaces = TableUtils.percentDecimals;

  currentFy: number = FiscalYearUtils.getCurrentFiscalYear();
  centerFy: number = this.currentFy;
  selectedOpl: number = this.currentFy;
  oplYearOptions: { value: number; label: string }[] = [];
  years: number[] = [];
  columns: TableColumn[] = [];
  groupedHeaders: GroupedHeaderCell[] = [];
  rows: MaintExpRow[] = [];
  availableProjects: Project[] = [];
  comboItems: MaintExpComboItem[] = [];
  selectedProjectId: number | null = null;
  editMode = false;

  private readonly touchedCells = new Set<string>();
  private originalRows: MaintExpRow[] = [];

  constructor(
    public defaults: DefaultValuesService,
    private readonly incoService: InvestmentControllingService,
    private readonly toastrService: ToastrService,
    private readonly customDialog: CustomDialogService,
  ) {
    this.buildOplYears();
  }

  get isOpl(): boolean {
    return this.mode === 'OPL';
  }

  get versionToken(): ProjectVersion {
    return this.isOpl ? 'BUDGET' : 'ACTUAL';
  }

  get inputFiscalYear(): number {
    return this.isOpl ? this.selectedOpl : this.currentFy;
  }

  ngOnInit(): void {
    this.rebuildYears();
    this.loadProjects();
    this.loadSavedRows();
  }

  private buildOplYears(): void {
    this.oplYearOptions = [];
    for (let y = this.currentFy - OPL_YEARS_BACK; y <= this.currentFy + OPL_YEARS_FORWARD; y++) {
      this.oplYearOptions.push({ value: y, label: `${y - 1}/${y} - OPL${y % 10}` });
    }
  }

  onCenterFyChange(year: number): void {
    this.centerFy = year;
    this.rebuildYears();
  }

  resetCenterFy(): void {
    if (this.centerFy === this.currentFy) return;
    this.centerFy = this.currentFy;
    this.rebuildYears();
  }

  onOplChange(year: number): void {
    const parsed = Number(year);
    if (parsed === this.selectedOpl) return;

    if (this.editMode && this.hasChanges()) {
      this.customDialog.openConfirm({
        title: 'Discard Changes',
        message: 'You have unsaved changes. Switching the fiscal year will discard them. Continue?',
        caller: this
      });
      this.customDialog.confirmed().subscribe(res => {
        if (res.confirmed) {
          this.applyOplChange(parsed);
        } else {
          const previous = this.selectedOpl;
          this.selectedOpl = parsed;
          setTimeout(() => (this.selectedOpl = previous));
        }
      });
      return;
    }

    this.applyOplChange(parsed);
  }

  private applyOplChange(year: number): void {
    this.selectedOpl = year;
    this.editMode = false;
    this.touchedCells.clear();
    this.rebuildYears();
    this.loadSavedRows();
  }

  private rebuildYears(): void {
    this.years = [];
    if (this.isOpl) {
      for (let i = 0; i < OPL_FORECAST_LABELS.length; i++) {
        this.years.push(this.selectedOpl + i);
      }
    } else {
      for (let y = this.centerFy - FY_WINDOW; y <= this.centerFy + FY_WINDOW; y++) {
        this.years.push(y);
      }
    }
    this.buildColumns();
  }

  private buildColumns(): void {
    const cols: TableColumn[] = [
      { key: 'id', header: 'ID', width: '6%', align: 'left', editable: false },
      { key: 'projectTitle', header: 'Project', width: '20%', align: 'left', editable: false },
    ];
    const groups: GroupedHeaderCell[] = [
      { label: '', colspan: 2 },
    ];
    this.years.forEach((year, idx) => {
      const isCurrent = this.isOpl ? idx === 0 : year === this.currentFy;
      const startHeaderCls = isCurrent ? 'year-start current-fy' : 'year-start';
      const endHeaderCls = isCurrent ? 'year-end current-fy' : 'year-end';
      const startCellCls = isCurrent ? 'year-start current-fy' : 'year-start';
      const endCellCls = isCurrent ? 'year-end current-fy' : 'year-end';
      cols.push(
        {
          key: `m_${year}`, header: 'M (%)',
          type: 'number', step: this.decimalStep, maxDecimals: this.decimalPlaces, editable: true, align: 'center', width: '5%',
          editFormatter: (v: any) => this.formatDecimalValue(v),
          headerClass: startHeaderCls,
          cellClass: () => startCellCls,
        },
        {
          key: `e_${year}`, header: 'E (%)',
          type: 'number', step: this.decimalStep, maxDecimals: this.decimalPlaces, editable: true, align: 'center', width: '5%',
          editFormatter: (v: any) => this.formatDecimalValue(v),
          headerClass: endHeaderCls,
          cellClass: () => endCellCls,
        },
      );
      const label = this.isOpl
        ? `${OPL_FORECAST_LABELS[idx]} · FY ${FiscalYearNavigatorComponent.formatFy(year)}`
        : `FY ${FiscalYearNavigatorComponent.formatFy(year)}`;
      groups.push({
        label,
        colspan: 2,
        align: 'center',
        className: isCurrent ? 'year-group current-fy' : 'year-group',
      });
    });
    cols.push({ key: 'actions', header: '', width: '4%', align: 'center', editable: false, headerClass: 'year-start', cellClass: () => 'year-start' });
    groups.push({ label: '', colspan: 1, className: 'year-group' });
    this.columns = cols;
    this.groupedHeaders = groups;
  }

  private loadProjects(): void {
    this.incoService.getAllProjects(
      this, false,
      (tthis: MaintenanceExpansionConfigComponent, result: Project[]) => {
        tthis.availableProjects = result ?? [];
        tthis.refreshComboItems();
      },
      (tthis: MaintenanceExpansionConfigComponent, error: any) => {
        console.error('Error loading projects:', error);
        tthis.toastrService.error('Failed to load projects');
      }
    );
  }

  private loadSavedRows(): void {
    this.incoService.getMaintenanceExpansion(
      this.versionToken,
      this.inputFiscalYear,
      this,
      (tthis: MaintenanceExpansionConfigComponent, result: any[]) => {
        tthis.rows = (result ?? []).map(p => {
          const row: MaintExpRow = { id: p.projectId, projectTitle: p.projectTitle };
          for (const entry of p.entries ?? []) {
            row[`m_${entry.fiscalYear}`] = entry.maintenance;
            row[`e_${entry.fiscalYear}`] = entry.expansion;
          }
          return row;
        });
        tthis.touchedCells.clear();
        tthis.originalRows = structuredClone(tthis.rows);
        tthis.refreshComboItems();
      },
      (tthis: MaintenanceExpansionConfigComponent, error: any) => {
        console.error('Error loading saved maintenance/expansion data:', error);
        tthis.rows = [];
        tthis.originalRows = [];
        tthis.touchedCells.clear();
        tthis.editMode = false;
        tthis.refreshComboItems();
        tthis.toastrService.error('Failed to load saved values');
      }
    );
  }

  hasChanges(): boolean {
    return this.touchedCells.size > 0;
  }

  private refreshComboItems(): void {
    const taken = new Set(this.rows.map(r => r.id));
    this.comboItems = this.availableProjects
      .filter(p => !taken.has(p.id))
      .map(p => ({ id: p.id, projectTitle: p.projectTitle, displayLabel: `${p.id} - ${p.projectTitle}` }));
  }

  addSelectedProject(): void {
    if (this.selectedProjectId == null) return;
    const project = this.availableProjects.find(p => p.id === this.selectedProjectId);
    if (!project) return;
    if (this.rows.some(r => r.id === project.id)) return;

    const projectId = project.id;
    this.selectedProjectId = null;

    this.incoService.addMaintenanceExpansionProject(
      projectId,
      this.versionToken,
      this,
      (tthis: MaintenanceExpansionConfigComponent) => {
        const row: MaintExpRow = { id: project.id, projectTitle: project.projectTitle };
        for (const year of tthis.years) {
          row[`m_${year}`] = null;
          row[`e_${year}`] = null;
        }
        tthis.rows = [...tthis.rows, row];
        tthis.originalRows = structuredClone(tthis.rows);
        tthis.refreshComboItems();
      },
      (tthis: MaintenanceExpansionConfigComponent, error: any) => {
        const message = error?.error?.message ?? error?.message ?? 'Failed to add project';
        tthis.toastrService.error(message, 'Add Failed');
      }
    );
  }

  removeRow(id: number): void {
    const row = this.rows.find(r => r.id === id);
    if (!row) return;

    this.customDialog.openConfirm({
      title: 'Remove Project',
      message: `Are you sure you want to remove "${row.projectTitle}" from the M/E table? All saved values will be lost.`,
      caller: this
    });

    this.customDialog.confirmed().subscribe(res => {
      if (!res.confirmed) return;

      this.incoService.removeMaintenanceExpansionProject(
        id,
        this.versionToken,
        this,
        (tthis: MaintenanceExpansionConfigComponent) => {
          tthis.rows = tthis.rows.filter(r => r.id !== id);
          for (const key of tthis.touchedCells) {
            if (key.startsWith(`${id}_`)) tthis.touchedCells.delete(key);
          }
          tthis.originalRows = structuredClone(tthis.rows);
          tthis.refreshComboItems();
        },
        (tthis: MaintenanceExpansionConfigComponent, error: any) => {
          const message = error?.error?.message ?? error?.message ?? 'Failed to remove project';
          tthis.toastrService.error(message, 'Remove Failed');
        }
      );
    });
  }

  onCellValueChange(event: { row: MaintExpRow; column: TableColumn; value: any }): void {
    if (event.column.type !== 'number') return;

    const key = event.column.key;
    const match = /^([me])_(\d{4})$/.exec(key);
    if (!match) return;
    const [, field, yearStr] = match;
    const year = Number(yearStr);

    const value = event.row[key];
    if (!this.isValidPercent(value)) return;

    const companionKey = `${field === 'm' ? 'e' : 'm'}_${year}`;
    // Never rewrite the typed cell (the input keeps showing the raw text and would silently
    // desync from the model) — values with too many decimals are rejected by isValidPercent.
    event.row[companionKey] = value == null || value === ''
      ? null
      : this.roundToScale(100 - Number(value));

    this.touchedCells.add(`${event.row.id}_${year}`);
  }

  formatDecimalValue(value: any): string {
    return TableUtils.formatDecimal(value, this.decimalPlaces, { emptyPlaceholder: '' });
  }

  formatCellValue(row: MaintExpRow, column: TableColumn): string {
    const value = row[column.key];
    if (value == null || value === '') return '-';
    return /^[me]_\d{4}$/.test(column.key) ? this.formatDecimalValue(value) : String(value);
  }

  private get decimalFactor(): number {
    return Math.pow(10, this.decimalPlaces);
  }

  get decimalStep(): string {
    return (1 / this.decimalFactor).toString();
  }

  private roundToScale(value: number): number {
    return Math.round(value * this.decimalFactor) / this.decimalFactor;
  }

  private isValidPercent(value: any): boolean {
    if (value == null || value === '') return true;
    const num = Number(value);
    if (Number.isNaN(num) || num < 0 || num > 100) return false;
    // No more decimal places than configured (default 1, e.g. 99.1).
    return Math.abs(num - this.roundToScale(num)) < 1e-9;
  }

  hasInvalidValues(): boolean {
    for (const row of this.rows) {
      for (const key of Object.keys(row)) {
        if (!/^[me]_\d{4}$/.test(key)) continue;
        if (!this.isValidPercent(row[key])) return true;
      }
    }
    return false;
  }

  onClickEditUndo(): void {
    if (this.editMode) {
      this.rows = structuredClone(this.originalRows);
      this.touchedCells.clear();
      this.editMode = false;
      this.refreshComboItems();
    } else if (this.rows.length > 0) {
      this.editMode = true;
    }
  }

  onClickSave(): void {
    if (!this.editMode || this.hasInvalidValues()) return;
    if (!this.hasChanges()) {
      this.editMode = false;
      return;
    }
    this.saveAll();
  }

  private saveAll(): void {
    const dirtyProjectIds = new Set<number>();
    for (const key of this.touchedCells) {
      const id = Number(key.split('_')[0]);
      if (!Number.isNaN(id)) dirtyProjectIds.add(id);
    }

    const payload: { projectId: number; projectTitle: string; entries: { projectId: number; fiscalYear: number; maintenance: number; expansion: number }[] }[] = [];

    for (const id of dirtyProjectIds) {
      const row = this.rows.find(r => r.id === id);
      if (!row) continue;

      const yearsInRow = Object.keys(row)
        .map(k => /^m_(\d{4})$/.exec(k))
        .filter((m): m is RegExpExecArray => m !== null)
        .map(m => Number(m[1]));

      const entries = yearsInRow
        .map(year => {
          const m = row[`m_${year}`];
          const e = row[`e_${year}`];
          const filled = m != null && m !== '' && e != null && e !== '';
          return filled
            ? { projectId: row.id, fiscalYear: year, maintenance: Number(m), expansion: Number(e) }
            : null;
        })
        .filter((x): x is { projectId: number; fiscalYear: number; maintenance: number; expansion: number } => x !== null);

      payload.push({ projectId: row.id, projectTitle: row.projectTitle, entries });
    }

    const projectCount = payload.length;
    this.incoService.saveMaintenanceExpansion(
      payload,
      this.versionToken,
      this.inputFiscalYear,
      this,
      (tthis: MaintenanceExpansionConfigComponent) => {
        tthis.toastrService.success(`Saved ${projectCount} project(s)`, 'Save Successful');
        tthis.editMode = false;
        tthis.loadSavedRows();
      },
      (tthis: MaintenanceExpansionConfigComponent, error: any) => {
        const message = error?.error?.message ?? error?.message ?? 'Save failed';
        tthis.toastrService.error(message, 'Save Failed');
      }
    );
  }
}

